import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Modal, Input, Select, Badge, Table } from "@/components/UI";
import { Plus, TrendingUp, Wallet, Factory } from "lucide-react";
import { openPdfPreview } from "@/lib/pdfPreview";
import { CAPAPanel } from "@/components/CAPAPanel";
import { todayTR } from "@/lib/date";

export function FinancePage() {
  const { user } = useAuth();
  // [msg-145 Bölüm 3+4] Finansal Analiz & Kârlılık yalnızca Sistem Yöneticisi'ne açıktır.
  const isAdmin = user?.role === "admin";
  // iter97 Fix Pkg 03/03 AA: Mali Analiz açıldığında default = Gerçek Kârlılık (profitability)
  const [tab, setTab] = useState("profitability");
  const [expenses, setExpenses] = useState([]);
  const [cats, setCats] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [period, setPeriod] = useState("month");
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({ eur_try_rate: 35 });
  const [labor, setLabor] = useState(null);
  const [formulas, setFormulas] = useState([]);
  const [products, setProducts] = useState([]);
  const empty = { category: "salary", kind: "monthly", amount: 0, month: new Date().toISOString().slice(0, 7), description: "" };
  const [f, setF] = useState(empty);
  const rate = parseFloat(settings.eur_try_rate) || 35;
  const eur = (try_val) => (try_val || 0) / rate;
  const fmt = (n, cur = "EUR") => `${(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ${cur}`;

  const load = () => Promise.all([api.get("/finance/expenses"), api.get("/finance/categories"), api.get("/finance/analysis", { period }), api.get("/settings"), api.get("/finance/labor-per-unit"), api.get("/formulas"), api.get("/products")])
    .then(([e, c, a, s, l, fo, p]) => { setExpenses(e); setCats(c); setAnalysis(a); setSettings(s); setLabor(l); setFormulas(fo); setProducts(p); });
  useEffect(() => { load(); }, [period]);

  const save = async () => { await api.post("/finance/expenses", f); setOpen(false); setF(empty); load(); };
  const del = async (id) => { if (window.confirm("Silinsin mi?")) { await api.del(`/finance/expenses/${id}`); load(); } };
  const refreshFx = async () => {
    try {
      const r = await api.post("/settings/refresh-fx-rate");
      alert(`TCMB kuru güncellendi: 1 EUR = ${r.eur_try_rate.toFixed(4)} TRY`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "TCMB kur alınamadı"); }
  };
  const catLabel = k => cats.find(c => c.key === k)?.label || k;

  return (
    <div>
      <PageHeader title="Finansal Analiz" description="EUR ana para birimi — Türk Lirası (TRY) ikinci gösterim" actions={<Btn onClick={() => setOpen(true)} data-testid="new-expense"><Plus size={14}/> Yeni Gider</Btn>} />
      {!isAdmin && (
        <Card className="p-6 text-center border-amber-300 bg-amber-50" data-testid="finance-locked">
          <div className="text-amber-800 font-semibold mb-2">🔒 Yetkisiz Erişim</div>
          <div className="text-sm text-amber-700">Finansal Analiz, Kârlılık ve İç Maliyet raporları yalnızca <b>Sistem Yöneticisi</b> tarafından görüntülenebilir.</div>
        </Card>
      )}
      {isAdmin && (<>
      <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-indigo-800">EUR/TRY Kuru:</span>
        <span className="px-3 py-1 bg-white border rounded font-mono text-sm" data-testid="fx-rate-display">1 EUR = {rate.toFixed(4)} TRY</span>
        <Btn size="sm" onClick={refreshFx} data-testid="fx-rate-refresh">TCMB'den Güncelle</Btn>
        <span className="text-xs text-slate-500 ml-auto">
          {settings.eur_try_rate_source ? `Kaynak: ${settings.eur_try_rate_source}` : "Manuel"} · {settings.eur_try_rate_updated_at ? settings.eur_try_rate_updated_at.slice(0, 16).replace("T", " ") : "—"} · her sabah 09:30 TR otomatik güncellenir
        </span>
      </div>
      <div className="border-b mb-4 flex gap-4">
        {/* iter97 Fix Pkg 03/03 AA: default sıra Gerçek Kârlılık (profitability) ilk. Üretim ve İleri Vadeli mevcut analysis içinde. Gider Listesi (expenses) Y maddesine göre Muhasebe → Gelir/Gider'e taşınacak — burada geçici olarak korundu, taşıma sonrası kaldırılacak. */}
        {[{ k: "profitability", l: "Gerçek Kârlılık" }, { k: "analysis", l: "Üretim / İleri Vadeli Analiz" }, { k: "expenses", l: `Giderler (${expenses.length})` }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-1 py-2 text-sm border-b-2 ${tab === t.k ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500"}`} data-testid={`fin-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === "analysis" && analysis && (
        <>
          <div className="mb-3">
            <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)} data-testid="fin-period">
              <option value="day">Bugün</option><option value="week">Bu Hafta</option><option value="month">Bu Ay</option><option value="year">Bu Yıl</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KPI label="Gelir" try_val={analysis.revenue} rate={rate} icon={TrendingUp} color="emerald" />
            <KPI label="Ham + Ambalaj" try_val={analysis.direct_cost} rate={rate} icon={Factory} />
            <KPI label="İşletme Gideri" try_val={analysis.op_expense} rate={rate} icon={Wallet} />
            <KPI label="Net Kâr" try_val={analysis.net_profit} rate={rate} color={analysis.net_profit >= 0 ? "emerald" : "red"} />
          </div>
          <Card className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Row l="Ham Madde" v={fmt(eur(analysis.raw_cost))} sub={fmt(analysis.raw_cost, "TRY")} />
              <Row l="Ambalaj" v={fmt(eur(analysis.pkg_cost))} sub={fmt(analysis.pkg_cost, "TRY")} />
              <Row l="Toplam Maliyet" v={fmt(eur(analysis.total_cost))} sub={fmt(analysis.total_cost, "TRY")} />
              <Row l="Marj %" v={`%${analysis.margin_pct}`} />
              <Row l="Günlük İşl. Gider" v={fmt(eur(analysis.daily_op_expense))} sub={fmt(analysis.daily_op_expense, "TRY")} />
              <Row l="Adet Bşn Overhead" v={fmt(eur(analysis.per_unit_op_expense))} sub={fmt(analysis.per_unit_op_expense, "TRY")} />
              <Row l="Üretilen" v={`${analysis.units_produced} adet`} />
              <Row l="Dolum" v={`${analysis.units_filled} adet`} />
              <Row l="Sevk Edilen" v={`${analysis.units_shipped} adet`} />
              <Row l="Faturalanan" v={`${analysis.units_invoiced} adet`} />
              <Row l="Dönem Gün" v={analysis.days} />
              <Row l="Brüt Kâr" v={fmt(eur(analysis.gross_profit))} sub={fmt(analysis.gross_profit, "TRY")} />
            </div>
          </Card>
        </>
      )}

      {tab === "profitability" && (
        <div data-testid="profitability-panel">
          <Card className="p-4 mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Row l="Toplam Gider (Tüm Zaman)" v={fmt(eur(labor?.total_expense_try))} sub={fmt(labor?.total_expense_try, "TRY")} />
              <Row l="Aktif Gün Sayısı" v={`${labor?.active_days || 0} gün`} />
              <Row l="Toplam Üretilen Adet" v={`${labor?.total_units_produced || 0}`} />
              <Row l="Günlük Ort. Üretim" v={`${(labor?.daily_avg_units || 0).toFixed(2)} adet`} />
              <Row l="Günlük Ort. Gider" v={fmt(labor?.daily_avg_expense_eur)} sub={fmt(labor?.daily_avg_expense_try, "TRY")} />
              <Row l="İşçilik / Adet" v={fmt(labor?.labor_per_unit_eur)} sub={fmt(labor?.labor_per_unit_try, "TRY")} />
            </div>
          </Card>
          <Table data-testid="profitability-table" columns={[
            { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
            { label: "Formül", key: "name" },
            { label: "Ürün", render: r => products.find(p => p.id === r.product_id)?.name || "—" },
            { label: "İç Maliyet (EUR)", render: r => {
              const prod = products.find(p => p.id === r.product_id);
              const fq = prod?.fill_qty || 0; const fu = prod?.fill_unit || "ml";
              const cpk = r.cost?.cost_per_kg || 0;
              const pk = r.cost?.packaging_cost_per_unit || 0;
              const raw = fu === "ml" ? cpk * (r.density || 1) * fq / 1000 : cpk * fq / 1000;
              return (raw + pk).toFixed(4);
            } },
            { label: "İşçilik (EUR)", render: () => (labor?.labor_per_unit_eur || 0).toFixed(4) },
            { label: "Satış (EUR)", render: r => (r.sales_price_eur || 0).toFixed(2) },
            { label: "Kâr / Marj", render: r => {
              const prod = products.find(p => p.id === r.product_id);
              const fq = prod?.fill_qty || 0; const fu = prod?.fill_unit || "ml";
              const cpk = r.cost?.cost_per_kg || 0;
              const pk = r.cost?.packaging_cost_per_unit || 0;
              const raw = fu === "ml" ? cpk * (r.density || 1) * fq / 1000 : cpk * fq / 1000;
              const internal = raw + pk;
              const sales = r.sales_price_eur || 0;
              if (sales <= 0) return <span className="text-slate-400">Satış girilmedi</span>;
              const profit = sales - internal - (labor?.labor_per_unit_eur || 0);
              const margin = sales > 0 ? (profit / sales * 100) : 0;
              return <span className={`font-mono font-semibold ${profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{profit.toFixed(2)} € <span className="text-[10px] text-slate-500">%{margin.toFixed(1)}</span></span>;
            } },
          ]} rows={formulas} />
        </div>
      )}

      {tab === "expenses" && (
        <Table columns={[
          { label: "Kategori", render: r => catLabel(r.category) },
          { label: "Tip", key: "kind" },
          { label: "Tutar (EUR)", render: r => fmt(eur(r.amount)) },
          { label: "Tutar (TRY)", render: r => fmt(r.amount, "TRY") },
          { label: "Ay", key: "month" },
          { label: "Açıklama", key: "description" },
          { label: "İşlem", render: r => <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`delete-exp-${r.id}`}>Sil</Btn> },
        ]} rows={expenses} />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Yeni Gider">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Kategori *" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>{cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</Select>
          <Select label="Tip *" value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })}>
            <option value="monthly">Aylık Sabit</option><option value="variable">Değişken</option><option value="one_time">Tek Seferlik</option>
          </Select>
          <Input label="Tutar (TRY) *" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: parseFloat(e.target.value) })} data-testid="exp-amount"/>
          <Input label="Ay (YYYY-MM)" value={f.month} onChange={e => setF({ ...f, month: e.target.value })} />
          <div className="col-span-2 text-xs text-slate-500 font-mono">≈ {fmt(eur(f.amount))}</div>
          <div className="col-span-2"><Input label="Açıklama" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-expense">Kaydet</Btn></div>
      </Modal>
      </>)}
    </div>
  );
}

function KPI({ label, try_val, rate, icon: Icon, color = "indigo" }) {
  const colors = { indigo: "text-indigo-600", emerald: "text-emerald-600", red: "text-red-600" };
  const eur_val = (try_val || 0) / (rate || 35);
  return (
    <Card className="p-4">
      <div className="flex justify-between items-center mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        {Icon && <Icon size={16} className={colors[color]} />}
      </div>
      <div className={`text-2xl font-bold ${colors[color]}`} style={{ fontFamily: "'Manrope', sans-serif" }}>{eur_val.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</div>
      <div className="text-xs text-slate-500 mt-0.5">EUR</div>
      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{(try_val || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} TRY</div>
    </Card>
  );
}
function Row({ l, v, sub }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5">
      <span className="text-slate-500">{l}</span>
      <div className="text-right">
        <div className="font-mono font-semibold text-slate-900">{v}</div>
        {sub && <div className="text-[10px] font-mono text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

export function RequestsPage() {
  const [tab, setTab] = useState("requests");
  const [rows, setRows] = useState([]);
  const [capaRows, setCapaRows] = useState([]);
  const [capaMonth, setCapaMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [notifs, setNotifs] = useState([]);
  const [expandedNotif, setExpandedNotif] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [sortBy, setSortBy] = useState("date_desc");
  const empty = { title: "", description: "", assigned_to: "", department: "", priority: "normal", status: "open" };
  const [f, setF] = useState(empty);
  const load = () => Promise.all([api.get("/requests"), api.get("/notifications")]).then(([r, n]) => {
    // Kullanıcı talepleri (manuel oluşturulmuş)
    setRows((r || []).filter(x => !x.capa_auto));
    // GMP CAPA otomatik talepler (kendi sekmesinde)
    setCapaRows((r || []).filter(x => x.capa_auto).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    // Bildirimler: yalnızca /notifications endpoint'i (CAPA'lar artık kendi sekmesinde)
    setNotifs((n || []).map(x => ({ ...x, description: x.description || "" })));
  });
  useEffect(() => { load(); }, []);
  const save = async () => { await api.post("/requests", f); setOpen(false); setF(empty); load(); };
  const setStatus = async (r, status) => {
    try { await api.put(`/requests/${r.id}`, { ...r, status }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Durum güncellenemedi"); }
  };
  const del = async (id) => { if (window.confirm("Silinsin mi?")) { await api.del(`/requests/${id}`); load(); } };
  const toggleExpand = (id) => { const s = new Set(expanded); s.has(id) ? s.delete(id) : s.add(id); setExpanded(s); };
  const toggleNotif = (key) => { const s = new Set(expandedNotif); s.has(key) ? s.delete(key) : s.add(key); setExpandedNotif(s); };

  const priorityRank = { critical: 0, high: 1, normal: 2, low: 3 };
  const sorted = React.useMemo(() => {
    const arr = [...rows];
    if (sortBy === "priority") arr.sort((a, b) => (priorityRank[a.priority] ?? 9) - (priorityRank[b.priority] ?? 9));
    else if (sortBy === "requester") arr.sort((a, b) => (a.created_by_name || "").localeCompare(b.created_by_name || "", "tr"));
    else if (sortBy === "status") arr.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
    else if (sortBy === "title") arr.sort((a, b) => (a.title || "").localeCompare(b.title || "", "tr"));
    else if (sortBy === "date_asc") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return arr;
  }, [rows, sortBy]); // eslint-disable-line

  const kindLabel = (k) => ({ stock: "Kritik Stok", expiry: "SKT Yaklaşan", qc: "QC Beklemede", gmp: "GMP Uygunsuzluğu", capa: "Otomatik CAPA", delay: "Geciken Sipariş" }[k] || k);
  const kindColor = (k) => ({ stock: "orange", expiry: "red", qc: "amber", gmp: "amber", capa: "rose", delay: "red" }[k] || "slate");
  const [notifFilter, setNotifFilter] = useState({ critical: false, today: false, gmp: false });
  const filteredNotifs = React.useMemo(() => {
    const todayStr = todayTR();
    return notifs.filter(n => {
      if (notifFilter.critical && !(n.severity === "critical" || n.severity === "missing")) return false;
      if (notifFilter.today && !(n.created_at || "").startsWith(todayStr)) return false;
      if (notifFilter.gmp && !["gmp", "capa"].includes(n.kind)) return false;
      return true;
    });
  }, [notifs, notifFilter]);
  const toggleFilter = (k) => setNotifFilter(f => ({ ...f, [k]: !f[k] }));

  return (
    <div>
      <PageHeader title="Talepler / Bildirimler"
        description="Kullanıcı talepleri ve sistem otomatik bildirimlerini tek yerde yönetin"
        actions={tab === "requests" ? <Btn onClick={() => setOpen(true)} data-testid="new-request"><Plus size={14}/> Yeni Talep</Btn> : null} />

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        <button onClick={() => setTab("requests")} data-testid="tab-requests"
          className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition ${tab === "requests" ? "border-amber-500 text-amber-700 bg-amber-50/40" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>
          Talepler <span className="text-xs bg-slate-200 text-slate-700 px-1.5 rounded-full">{rows.length}</span>
        </button>
        <button onClick={() => setTab("capa")} data-testid="tab-capa"
          className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition ${tab === "capa" ? "border-amber-600 text-amber-800 bg-amber-100/60" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>
          🛡 GMP CAPA <span className="text-xs bg-amber-200 text-amber-900 px-1.5 rounded-full">{capaRows.length}</span>
        </button>
        <button onClick={() => setTab("notifs")} data-testid="tab-notifs"
          className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition ${tab === "notifs" ? "border-rose-500 text-rose-700 bg-rose-50/40" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>
          Bildirimler <span className="text-xs bg-rose-200 text-rose-800 px-1.5 rounded-full">{notifs.length}</span>
        </button>
      </div>

      {tab === "capa" && (
        <CAPAPanel testidPrefix="capa"/>
      )}

      {tab === "requests" && (
      <div>
      <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-2" data-testid="request-sort-bar">
        <div className="text-slate-600 flex items-center gap-1 text-xs font-semibold uppercase mr-1">Sırala</div>
        <div className="w-56">
          <Select label="Sıralama Ölçütü" value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="request-sort-select">
            <option value="date_desc">Tarih (Yeni → Eski)</option><option value="date_asc">Tarih (Eski → Yeni)</option>
            <option value="priority">Öncelik (Kritik → Düşük)</option><option value="requester">Talep Eden (A → Z)</option>
            <option value="status">Durum</option><option value="title">Başlık (A → Z)</option>
          </Select>
        </div>
        <div className="flex-1"/>
        <div className="text-xs text-slate-500 pb-1.5">{sorted.length} kayıt</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs">
            <tr>
              <th className="px-3 py-2 w-6"></th>
              <th className="text-left px-3 py-2">No</th>
              <th className="text-left px-3 py-2">Başlık</th>
              <th className="text-left px-3 py-2">Departman</th>
              <th className="text-left px-3 py-2">Talep Eden</th>
              <th className="text-left px-3 py-2">Öncelik</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-left px-3 py-2">Tarih</th>
              <th className="text-left px-3 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const isOpen = expanded.has(r.id);
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => toggleExpand(r.id)} data-testid={`req-row-${r.id}`}>
                    <td className="px-3 py-2 text-slate-500">{isOpen ? "▾" : "▸"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.title}</td>
                    <td className="px-3 py-2">{r.department || "—"}</td>
                    <td className="px-3 py-2">{r.created_by_name || "—"}</td>
                    <td className="px-3 py-2"><Badge level={r.priority === "critical" ? "missing" : r.priority === "high" ? "critical" : r.priority === "normal" ? "attention" : "inactive"}>{r.priority}</Badge></td>
                    <td className="px-3 py-2"><Badge level={r.status === "completed" ? "ok" : r.status === "cancelled" ? "inactive" : "attention"}>{r.status}</Badge></td>
                    <td className="px-3 py-2 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {r.status === "open" && <Btn size="sm" variant="secondary" onClick={() => setStatus(r, "in_progress")}>Başla</Btn>}
                        {r.status !== "completed" && <Btn size="sm" onClick={() => setStatus(r, "completed")} data-testid={`req-complete-${r.id}`}>Tamamla</Btn>}
                        <Btn size="sm" variant="danger" onClick={() => del(r.id)}>Sil</Btn>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50 border-t border-slate-100" data-testid={`req-detail-${r.id}`}>
                      <td colSpan={9} className="px-6 py-4">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="text-slate-500 uppercase font-semibold mb-1">Açıklama</div>
                            <div className="bg-white border border-slate-200 rounded p-3 text-slate-700 whitespace-pre-wrap min-h-[80px]">{r.description || <span className="italic text-slate-400">Açıklama girilmemiş.</span>}</div>
                          </div>
                          <div className="space-y-1.5">
                            <div><span className="text-slate-500">Atanan:</span> <span className="font-semibold">{r.assigned_to || "—"}</span></div>
                            <div><span className="text-slate-500">Talep Eden:</span> <span className="font-semibold">{r.created_by_name || "—"}</span></div>
                            <div><span className="text-slate-500">Oluşturma:</span> <span className="font-mono">{(r.created_at || "").slice(0, 19).replace("T", " ")}</span></div>
                            {r.completed_at && <div><span className="text-slate-500">Tamamlanma:</span> <span className="font-mono">{r.completed_at.slice(0, 19).replace("T", " ")}</span></div>}
                            {r.capa_auto && <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-amber-800"><b>⚠ Otomatik CAPA:</b> Bu talep GMP uygunsuzluğu ({r.capa_source}) sonucu otomatik açıldı.</div>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {sorted.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400 text-sm">Kayıt yok.</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Yeni Talep" size="lg">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Başlık *" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="req-title"/></div>
          <Input label="Departman" value={f.department} onChange={e => setF({ ...f, department: e.target.value })} />
          <Input label="Atanan" value={f.assigned_to} onChange={e => setF({ ...f, assigned_to: e.target.value })} />
          <Select label="Öncelik" value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}>
            <option value="low">Düşük</option><option value="normal">Normal</option><option value="high">Yüksek</option><option value="critical">Kritik</option>
          </Select>
          <div className="col-span-2"><textarea placeholder="Açıklama (talep detaylarını yazın — liste üzerinde başlığa tıklandığında görünür)" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" rows="4" data-testid="req-desc"/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-request">Kaydet</Btn></div>
      </Modal>
      </div>
      )}

      {tab === "notifs" && (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-3 bg-slate-50/60 border border-slate-200 rounded-lg p-2" data-testid="notif-filter-bar">
            <span className="text-[10px] uppercase font-semibold text-slate-500 mr-1">Hızlı Filtre</span>
            <button onClick={() => toggleFilter("critical")} data-testid="notif-filter-critical"
              className={`text-xs px-2.5 py-1 rounded-full border transition ${notifFilter.critical ? "bg-red-500 border-red-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-red-400"}`}>
              🚨 Sadece Kritik
            </button>
            <button onClick={() => toggleFilter("today")} data-testid="notif-filter-today"
              className={`text-xs px-2.5 py-1 rounded-full border transition ${notifFilter.today ? "bg-indigo-500 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-indigo-400"}`}>
              📅 Sadece Bugün
            </button>
            <button onClick={() => toggleFilter("gmp")} data-testid="notif-filter-gmp"
              className={`text-xs px-2.5 py-1 rounded-full border transition ${notifFilter.gmp ? "bg-amber-500 border-amber-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-amber-400"}`}>
              🧼 Sadece GMP
            </button>
            {(notifFilter.critical || notifFilter.today || notifFilter.gmp) && (
              <button onClick={() => setNotifFilter({ critical: false, today: false, gmp: false })} className="text-xs text-slate-500 hover:text-slate-800 underline">Temizle</button>
            )}
            <div className="ml-auto text-xs text-slate-500">{filteredNotifs.length} / {notifs.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="notifs-panel">
            {filteredNotifs.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">✓ Filtreye uygun sistem bildirimi yok.</div>}
            {filteredNotifs.map((n, i) => {
            const key = `${n.kind}-${n.source_id || i}`;
            const isOpen = expandedNotif.has(key);
            const color = kindColor(n.kind);
            return (
              <div key={key} className={`border-b border-slate-100 last:border-b-0 ${isOpen ? "bg-slate-50" : "bg-white"}`}>
                <div className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 border-l-4 border-${color}-500`} onClick={() => toggleNotif(key)} data-testid={`notif-row-${i}`}>
                  <span className="text-slate-400 text-xs">{isOpen ? "▾" : "▸"}</span>
                  <span className={`text-[10px] uppercase font-bold tracking-wider text-${color}-700 bg-${color}-50 px-2 py-0.5 rounded border border-${color}-200 min-w-[110px] text-center`}>{kindLabel(n.kind)}</span>
                  <div className="flex-1 text-sm text-slate-800 font-medium">{n.message}</div>
                  <span className="text-[10px] text-slate-400">{(n.created_at || "").slice(0, 10) || "—"}</span>
                  <Badge level={n.severity === "critical" ? "critical" : n.severity === "missing" ? "missing" : n.severity === "attention" ? "attention" : "ok"}>{n.severity || "info"}</Badge>
                </div>
                {isOpen && (
                  <div className="px-6 pb-4 pt-1 bg-slate-50" data-testid={`notif-detail-${i}`}>
                    <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Açıklama / Detay</div>
                    <div className="bg-white border border-slate-200 rounded p-3 text-xs text-slate-700 whitespace-pre-wrap">
                      {n.description || <span className="italic text-slate-400">Bu bildirim otomatik oluşturuldu, ek açıklama yok.</span>}
                    </div>
                    {n.link && n.link !== "/requests" && (
                      <div className="mt-2 text-xs"><a href={n.link} className="text-blue-600 hover:underline">→ İlgili sayfaya git</a></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}
